import React from 'react';
import { ChevronDownIcon } from 'lucide-react';

interface FunnelData {
  opportunities_discovered?: number;
  comments_generated?: number;
  comments_posted?: number;
  leads?: number;
  qualified_leads?: number;
  demos?: number;
  customers?: number;
  conversion_rates?: {
    comment_generation_rate?: number;
    posting_rate?: number;
    lead_rate?: number;
    qualification_rate?: number;
    demo_rate?: number;
    close_rate?: number;
  };
}

interface ConversionFunnelProps {
  data?: FunnelData;
}

export default function ConversionFunnel({ data }: ConversionFunnelProps) {
  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Loading conversion funnel...</p>
      </div>
    );
  }

  const stages = [
    {
      name: 'Opportunities Discovered',
      value: data.opportunities_discovered || 0,
      rate: 100,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700',
    },
    {
      name: 'Comments Generated',
      value: data.comments_generated || 0,
      rate: data.conversion_rates?.comment_generation_rate || 0,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-700',
    },
    {
      name: 'Comments Posted',
      value: data.comments_posted || 0,
      rate: data.conversion_rates?.posting_rate || 0,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      textColor: 'text-green-700',
    },
    {
      name: 'Leads',
      value: data.leads || 0,
      rate: data.conversion_rates?.lead_rate || 0,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-700',
    },
    {
      name: 'Qualified Leads',
      value: data.qualified_leads || 0,
      rate: data.conversion_rates?.qualification_rate || 0,
      color: 'from-yellow-500 to-yellow-600',
      bgColor: 'bg-yellow-50',
      textColor: 'text-yellow-700',
    },
    {
      name: 'Demos',
      value: data.demos || 0,
      rate: data.conversion_rates?.demo_rate || 0,
      color: 'from-pink-500 to-pink-600',
      bgColor: 'bg-pink-50',
      textColor: 'text-pink-700',
    },
    {
      name: 'Customers',
      value: data.customers || 0,
      rate: data.conversion_rates?.close_rate || 0,
      color: 'from-emerald-500 to-emerald-600',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-700',
    },
  ];

  // Calculate widths based on the first stage (100%)
  const maxValue = stages[0].value || 1;

  return (
    <div className="space-y-4">
      {stages.map((stage, index) => {
        const widthPercentage = maxValue > 0 ? (stage.value / maxValue) * 100 : 0;
        const showStage = index === 0 || stage.value > 0;

        if (!showStage) return null;

        return (
          <div key={stage.name} className="space-y-2">
            {/* Stage Bar */}
            <div className="relative">
              <div
                className={`rounded-lg ${stage.bgColor} p-4 transition-all duration-300 hover:shadow-md`}
                style={{ width: `${Math.max(widthPercentage, 10)}%` }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600">{stage.name}</p>
                    <p className={`text-2xl font-bold ${stage.textColor} mt-1`}>
                      {stage.value.toLocaleString()}
                    </p>
                  </div>
                  {index > 0 && (
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Conversion Rate</p>
                      <p className={`text-lg font-semibold ${stage.textColor}`}>
                        {stage.rate.toFixed(1)}%
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Arrow between stages */}
            {index < stages.length - 1 && stages[index + 1].value > 0 && (
              <div className="flex justify-center">
                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
              </div>
            )}
          </div>
        );
      })}

      {/* Summary Stats */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Overall Performance</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-600">Discovery → Post</p>
            <p className="text-lg font-bold text-gray-900">
              {data.conversion_rates?.posting_rate?.toFixed(1)}%
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-600">Post → Lead</p>
            <p className="text-lg font-bold text-gray-900">
              {data.conversion_rates?.lead_rate?.toFixed(1)}%
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-600">Lead → Demo</p>
            <p className="text-lg font-bold text-gray-900">
              {data.conversion_rates?.demo_rate?.toFixed(1)}%
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-600">Demo → Customer</p>
            <p className="text-lg font-bold text-gray-900">
              {data.conversion_rates?.close_rate?.toFixed(1)}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
